// Multi-family: Buildings CRUD page (P1-10).
//
// Local-only banner stays on (UniFi enforcement lane wires later, but
// physical-intent mutations already carry an idempotency key per-modal
// so the controller push can collapse retries when it lands).

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Button, Icon } from '@ng-unifi/ui';
import { CreateBuildingBody, UpdateBuildingBody } from '@ng-unifi/domain';
import { apiClient, ApiError } from '../lib/api.js';
import { useIdempotencyKey } from '../lib/useIdempotencyKey.js';
import { Link } from 'react-router-dom';
import { CrudTable, fieldCls } from '../components/CrudTable.js';
import { scopedHref } from '../components/PropertyScope.js';
import { CrudModal, FormField } from '../components/CrudModal.js';
import { RowActions, Toast } from '../components/RowActions.js';
import { LocalOnlyBanner } from '../components/LocalOnlyBanner.js';

interface BuildingRow {
  id: string;
  name: string;
  address: string | null;
  created_at: string;
}

async function loadBuildings(): Promise<BuildingRow[]> {
  const res = await apiClient.get<{ data: BuildingRow[] }>('/api/v1/buildings');
  return res.data;
}

type ModalKind = 'create' | 'edit' | 'delete';
type ModalState = { kind: ModalKind; row: BuildingRow | null };

export function BuildingsPage() {
  const [rows, setRows] = useState<BuildingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    loadBuildings()
      .then((d) => {
        if (!cancelled) setRows(d);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Failed to load');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [reloadTick]);

  return (
    <div className="space-y-6">
      {/* flex-wrap + min-w-0: at 320px the icon, title and the action button
          cannot share one row, and the button is what has to move. Same
          header shape as HouseholdsPage. */}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-primary-container text-on-primary">
            <Icon name="apartment" className="text-2xl" />
          </div>
          <div className="min-w-0">
            <h1 className="font-headline-md text-headline-md text-on-surface">
              Buildings
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Physical buildings at this property.
            </p>
          </div>
        </div>
        <Button onClick={() => setModal({ kind: 'create', row: null })}>
          <Icon name="add" />
          <span>Add building</span>
        </Button>
      </header>

      <LocalOnlyBanner
        title="Local records only"
        body="Buildings are stored locally — controller-side topology binding lands in a follow-up tranche."
      />

      {toast ? <Toast message={toast} onDismiss={() => setToast(null)} /> : null}

      <CrudTable<BuildingRow>
        rows={rows}
        error={error}
        emptyMessage="No buildings yet. Click Add building to create one."
        columns={[
          {
            key: 'name',
            header: 'Name',
            width: '30%',
            // A building is the way into its units. It used to be a dead end:
            // this page had no outgoing links at all, so Units needed its own
            // nav row to be reachable.
            render: (b) => (
              <Link
                to={scopedHref('/units', { building_id: b.id })}
                className="rounded-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                {b.name}
              </Link>
            ),
          },
          {
            key: 'address',
            header: 'Address',
            render: (b) =>
              b.address ?? <span className="text-on-surface-variant">—</span>,
          },
          {
            key: 'created_at',
            header: 'Added',
            width: '18%',
            render: (b) => new Date(b.created_at).toLocaleDateString(),
          },
        ]}
        actions={(b) => (
          <RowActions
            actions={[
              { label: 'Edit', onClick: () => setModal({ kind: 'edit', row: b }) },
              {
                label: 'Delete',
                destructive: true,
                onClick: () => setModal({ kind: 'delete', row: b }),
              },
            ]}
          />
        )}
      />

      {modal ? (
        <BuildingModal
          state={modal}
          onCancel={() => setModal(null)}
          onDone={(msg) => {
            setModal(null);
            setToast(msg);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function BuildingModal({
  state,
  onCancel,
  onDone,
}: {
  state: ModalState;
  onCancel: () => void;
  onDone: (toast: string) => void;
}) {
  const { kind, row } = state;
  const { key: idemKey } = useIdempotencyKey();
  const [name, setName] = useState(row?.name ?? '');
  const [address, setAddress] = useState(row?.address ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (kind === 'create') {
        const body = CreateBuildingBody.parse({
          name: name.trim(),
          address: address.trim() || null,
        });
        await apiClient
          .withIdempotencyKey(idemKey)
          .post('/api/v1/buildings', body);
        onDone(`Building "${body.name}" created.`);
        return;
      }
      if (kind === 'edit') {
        const body = UpdateBuildingBody.parse({
          name: name.trim(),
          address: address.trim() || null,
        });
        await apiClient
          .withIdempotencyKey(idemKey)
          .patch(`/api/v1/buildings/${row!.id}`, body);
        onDone(`Building "${name.trim()}" updated.`);
        return;
      }
      if (kind === 'delete') {
        await apiClient.delete(`/api/v1/buildings/${row!.id}`);
        onDone(`Building "${row!.name}" deleted.`);
        return;
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed');
      setSubmitting(false);
    }
  }

  const title =
    kind === 'create'
      ? 'Add building'
      : kind === 'edit'
        ? `Edit ${row?.name}`
        : `Delete ${row?.name}`;
  const icon = kind === 'delete' ? 'delete' : kind === 'edit' ? 'edit' : 'add';
  const tone: 'default' | 'destructive' = kind === 'delete' ? 'destructive' : 'default';
  const submitLabel = kind === 'delete' ? 'Delete' : kind === 'edit' ? 'Save' : 'Create';

  return (
    <CrudModal
      title={title}
      icon={icon}
      tone={tone}
      submitting={submitting}
      error={error}
      submitLabel={submitLabel}
      canSubmit={kind === 'delete' || name.trim().length > 0}
      onCancel={onCancel}
      onSubmit={onSubmit}
    >
      {kind === 'delete' ? (
        <p className="font-body-md text-body-md text-on-surface">
          Deleting <strong>{row?.name}</strong> removes it from the directory.
          Units under this building must already be removed.
        </p>
      ) : (
        <>
          <FormField id="building-name" label="Name">
            <input
              id="building-name"
              required
              maxLength={120}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={fieldCls}
            />
          </FormField>
          <FormField id="building-address" label="Address" hint="Optional.">
            <input
              id="building-address"
              maxLength={500}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className={fieldCls}
            />
          </FormField>
        </>
      )}
    </CrudModal>
  );
}
